import { useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, Inbox, HandCoins, Mail, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/site";

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("involvements");
  const [involvements, setInvolvements] = useState([]);
  const [donations, setDonations] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upCategory, setUpCategory] = useState("fbla");
  const [upCaption, setUpCaption] = useState("");

  const headers = { "X-Admin-Token": token };

  const loadGallery = async (tk) => {
    try {
      const r = await axios.get(`${API}/gallery`);
      setGallery(r.data);
    } catch { /* ignore */ }
  };

  const load = async (t) => {
    setLoading(true);
    try {
      const [inv, don, sub] = await Promise.all([
        axios.get(`${API}/admin/involvements`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/donations`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/subscribers`, { headers: { "X-Admin-Token": t } }),
      ]);
      setInvolvements(inv.data);
      setDonations(don.data);
      setSubscribers(sub.data);
      await loadGallery(t);
      setAuthed(true);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Invalid admin token." : "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("category", upCategory);
    fd.append("caption", upCaption);
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/admin/gallery/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded.");
      setUpCaption("");
      await loadGallery(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: { "X-Admin-Token": token } });
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Photo removed.");
    } catch {
      toast.error("Could not remove photo.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A1128] grain flex items-center justify-center px-6">
        <div className="relative z-10 w-full max-w-sm bg-[#F8F9FA] p-10 text-center">
          <ShieldCheck className="mx-auto text-[#D4AF37]" size={40} />
          <h1 className="mt-5 font-display text-3xl text-[#0A1128]">Admin Access</h1>
          <p className="mt-2 text-sm text-[#4B5563]">Enter the admin token to view submissions.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(token)}
            placeholder="Admin token"
            className="mt-6 w-full px-4 py-3 border border-[#0A1128]/15 focus:border-[#D4AF37] outline-none text-center"
          />
          <button
            data-testid="admin-login-button"
            onClick={() => load(token)}
            disabled={loading || !token}
            className="mt-4 w-full py-3 bg-[#0A1128] text-white font-semibold hover:bg-[#1D3557] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter"}
          </button>
        </div>
      </div>
    );
  }

  const Tab = ({ id, label, icon: Icon, count }) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-5 py-3 border-b-2 transition-colors ${
        tab === id ? "border-[#D4AF37] text-[#0A1128]" : "border-transparent text-[#4B5563]"
      }`}
    >
      <Icon size={18} /> {label}
      <span className="ml-1 text-xs bg-[#0A1128] text-white px-2 py-0.5">{count}</span>
    </button>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      <div className="bg-[#0A1128] text-white px-6 md:px-10 py-6">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <h1 className="font-display text-2xl">fohamu · Admin</h1>
          <button onClick={() => load(token)} className="text-sm text-[#D4AF37]" data-testid="admin-refresh">Refresh</button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex gap-2 border-b border-[#0A1128]/10 mt-6">
          <Tab id="involvements" label="Get Involved" icon={Inbox} count={involvements.length} />
          <Tab id="donations" label="Donations" icon={HandCoins} count={donations.length} />
          <Tab id="subscribers" label="Subscribers" icon={Mail} count={subscribers.length} />
          <Tab id="gallery" label="Gallery" icon={ImageIcon} count={gallery.length} />
        </div>

        <div className="py-8">
          {tab === "involvements" && (
            <div className="space-y-4" data-testid="involvements-list">
              {involvements.length === 0 && <p className="text-[#4B5563]">No submissions yet.</p>}
              {involvements.map((i) => (
                <div key={i.id} className="bg-white p-6 border border-[#0A1128]/10">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-lg text-[#0A1128]">{i.name}</span>
                      <span className="text-xs uppercase tracking-widest bg-[#D4AF37]/20 text-[#0A1128] px-2 py-0.5">{i.role}</span>
                    </div>
                    <span className="text-xs text-[#4B5563]">{new Date(i.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#1D3557]">{i.email}{i.organization ? ` · ${i.organization}` : ""}</p>
                  <p className="mt-3 text-[#4B5563] leading-relaxed">{i.message}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "donations" && (
            <div className="bg-white border border-[#0A1128]/10 overflow-x-auto" data-testid="donations-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0A1128] text-white text-left">
                    <th className="px-4 py-3">Donor</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Frequency</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-[#4B5563]">No donations yet.</td></tr>
                  )}
                  {donations.map((d) => (
                    <tr key={d.session_id} className="border-t border-[#0A1128]/10">
                      <td className="px-4 py-3">{d.donor_name || "—"}<br /><span className="text-xs text-[#4B5563]">{d.donor_email}</span></td>
                      <td className="px-4 py-3 font-medium">${d.amount}</td>
                      <td className="px-4 py-3">{d.frequency}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs ${d.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                          {d.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#4B5563]">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "subscribers" && (
            <div className="bg-white border border-[#0A1128]/10" data-testid="subscribers-list">
              {subscribers.length === 0 && <p className="px-4 py-6 text-[#4B5563]">No subscribers yet.</p>}
              {subscribers.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-4 py-3 border-t border-[#0A1128]/10 first:border-t-0">
                  <span className="text-[#0A1128]">{s.email}</span>
                  <span className="text-xs text-[#4B5563]">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "gallery" && (
            <div data-testid="gallery-admin">
              <div className="bg-white border border-[#0A1128]/10 p-6 mb-6">
                <h3 className="font-display text-xl text-[#0A1128]">Upload a photo</h3>
                <p className="text-sm text-[#4B5563] mt-1">JPG, PNG, GIF or WEBP · up to 8MB. Appears on the public gallery instantly.</p>
                <div className="mt-5 flex flex-col md:flex-row gap-4 md:items-end">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">Category</label>
                    <select data-testid="gallery-category" value={upCategory} onChange={(e) => setUpCategory(e.target.value)}
                      className="mt-2 block w-full md:w-44 px-4 py-3 border border-[#0A1128]/20 bg-white outline-none focus:border-[#D4AF37]">
                      <option value="fbla">FBLA Leadership</option>
                      <option value="icdh">ICDH Student Body</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">Caption (optional)</label>
                    <input data-testid="gallery-caption" value={upCaption} onChange={(e) => setUpCaption(e.target.value)}
                      placeholder="e.g. FBLA state competition"
                      className="mt-2 w-full px-4 py-3 border border-[#0A1128]/20 outline-none focus:border-[#D4AF37]" />
                  </div>
                  <label data-testid="gallery-upload-btn" className={`inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0A1128] text-white font-semibold cursor-pointer hover:bg-[#1D3557] transition-colors ${uploading ? "opacity-70 pointer-events-none" : ""}`}>
                    {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                    {uploading ? "Uploading…" : "Choose & Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="gallery-file-input" />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="gallery-admin-list">
                {gallery.length === 0 && <p className="text-[#4B5563]">No uploaded photos yet.</p>}
                {gallery.map((g) => (
                  <div key={g.id} className="relative group border border-[#0A1128]/10 bg-white">
                    <img src={`${API}/gallery/file/${g.storage_path}`} alt={g.caption || "Gallery"} className="w-full aspect-[4/3] object-cover" />
                    <div className="px-3 py-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-[#4B5563] truncate">
                        <span className="uppercase text-[#B8901F] font-semibold">{g.category}</span>{g.caption ? ` · ${g.caption}` : ""}
                      </span>
                      <button data-testid={`gallery-delete-${g.id}`} onClick={() => handleDeleteImage(g.id)}
                        className="text-[#4B5563] hover:text-red-600 transition-colors shrink-0">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
